/**
 * 后台应用根：主题 → 词条（admin.* 与各后台命名空间，四语）→ 数字与日期格式（UTC+9）→ 屏幕宽度检查（≥1280px）→ 数据请求 → 轻提示 → 路由。
 */
import { I18nProvider, useI18n } from '@nexora/i18n/react';
import { adminSeedLoader } from '@nexora/i18n/bundled-admin';
import type { Lang } from '@nexora/i18n';
import { LocaleProvider, NumberSymbolsProvider, ThemeProvider, ToastProvider } from '@nexora/ui';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { RouterProvider, type createBrowserRouter } from 'react-router-dom';
import { bindAdminApiRouter } from './components/layout/api/client';
import { createAdminQueryClient } from './components/layout/api/query';
import { ADMIN_TIME_ZONE } from './components/layout/format';
import { WidthGate } from './components/layout/WidthGate';
import { loadAdminFonts } from './i18n/fonts';

type AdminRouter = ReturnType<typeof createBrowserRouter>;

function LocalizedProviders({ children }: { children: ReactNode }) {
  const { symbols, lang } = useI18n();
  // 后台时间一律按 UTC+9 显示（spec-platform §0.3）
  const locale = useMemo(() => ({ lang, timeZone: ADMIN_TIME_ZONE }), [lang]);
  useEffect(() => {
    void loadAdminFonts(lang);
  }, [lang]);
  return (
    <NumberSymbolsProvider symbols={symbols}>
      <LocaleProvider value={locale}>{children}</LocaleProvider>
    </NumberSymbolsProvider>
  );
}

export interface AdminAppProps {
  router: AdminRouter;
  initialLang: Lang;
  queryClient?: QueryClient;
}

export function AdminApp({ router, initialLang, queryClient: providedClient }: AdminAppProps) {
  const [queryClient] = useState(() => providedClient ?? createAdminQueryClient());
  // API 客户端的 401 跳转走这个路由（不刷新页面）
  useEffect(() => bindAdminApiRouter(router), [router]);
  return (
    <ThemeProvider defaultPreference="dark">
      <I18nProvider initialLang={initialLang} surface="admin" loader={adminSeedLoader}>
        <LocalizedProviders>
          <WidthGate>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
              </ToastProvider>
            </QueryClientProvider>
          </WidthGate>
        </LocalizedProviders>
      </I18nProvider>
    </ThemeProvider>
  );
}
